"use client";

import {
	AnimatePresence,
	motion,
	useInView,
	type Variants,
} from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, Star } from "lucide-react";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import type { SerpApiResponse } from "@/lib/google-places";
import { cn } from "@/lib/utils";

const SECTION_CONTENT = {
	ratingText: "/5 Average Rating",
	reviewsText: "reviews",
	title: "Hear from our #IAMFAM.",
	ctaText: "Read All Reviews on Google",
	verifiedText: "Google Review",
	defaultRating: 4.9,
	defaultTotalRatings: 9000,
	defaultGmapsUrl: "https://maps.google.com",
} as const;

const fadeInVariants: Variants = {
	hidden: { opacity: 0, y: 30 },
	visible: {
		opacity: 1,
		y: 0,
		transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
	},
};

interface GoogleReviewsProps {
	data?: SerpApiResponse;
}

export function GoogleReviews({ data }: GoogleReviewsProps) {
	const sectionRef = useRef<HTMLElement>(null);
	const isInView = useInView(sectionRef, { once: true, amount: 0.1 });

	const [currentIndex, setCurrentIndex] = useState(0);
	const [direction, setDirection] = useState(0);

	const reviews = data?.reviews || [];
	const rating = data?.place_info?.rating || SECTION_CONTENT.defaultRating;
	const totalRatings =
		data?.place_info?.reviews || SECTION_CONTENT.defaultTotalRatings;
	const gmapsUrl =
		data?.search_metadata?.google_maps_reviews_url ||
		SECTION_CONTENT.defaultGmapsUrl;

	const cardsToShowDesktop = 3;
	const _maxIndex = Math.max(0, reviews.length - 1);

	// biome-ignore lint/correctness/useExhaustiveDependencies: Timer logic
	useEffect(() => {
		if (reviews.length === 0) return;

		const interval = setInterval(() => {
			handleNext();
		}, 6000);
		return () => clearInterval(interval);
	}, [reviews.length, currentIndex]);

	const handleNext = () => {
		setDirection(1);
		setCurrentIndex((prev) => (prev + 1) % reviews.length);
	};

	const handlePrev = () => {
		setDirection(-1);
		setCurrentIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
	};

	const getVisibleReviews = () => {
		const result = [];
		for (let i = 0; i < cardsToShowDesktop; i++) {
			result.push(reviews[(currentIndex + i) % reviews.length]);
		}
		return result;
	};

	const mobileReview = reviews[currentIndex];
	const desktopReviews = getVisibleReviews();

	return (
		<section
			id="reviews"
			ref={sectionRef}
			className="py-16 md:py-24 bg-background text-foreground overflow-hidden relative"
		>
			<div className="container mx-auto px-4 relative z-10 mb-10 text-center">
				<motion.div
					initial="hidden"
					animate={isInView ? "visible" : "hidden"}
					variants={fadeInVariants}
					className="max-w-2xl mx-auto space-y-4"
				>
					<div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/50 backdrop-blur text-sm font-bold border border-border/50">
						<Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
						{rating}
						{SECTION_CONTENT.ratingText} ({totalRatings.toLocaleString()}{" "}
						{SECTION_CONTENT.reviewsText})
					</div>
					<h2 className="text-4xl md:text-5xl font-black">
						{SECTION_CONTENT.title}
					</h2>
				</motion.div>
			</div>

			<motion.div
				initial={{ opacity: 0 }}
				animate={isInView ? { opacity: 1 } : { opacity: 0 }}
				transition={{ duration: 0.6, delay: 0.2 }}
				className="relative w-full px-2 md:px-6 lg:px-8 xl:px-12 pb-10 flex flex-col items-center"
			>
				{reviews.length > 0 ? (
					<>
						<div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full relative min-h-75">
							<AnimatePresence mode="popLayout" custom={direction}>
								{desktopReviews.map((review) => (
									<motion.div
										key={`${review.user?.name}-${review.snippet?.slice(0, 20)}-${currentIndex}`}
										layout
										initial={{ opacity: 0, x: direction * 50 }}
										animate={{ opacity: 1, x: 0 }}
										exit={{ opacity: 0, x: direction * -50 }}
										transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
										className="h-full"
									>
										<ReviewCard review={review} />
									</motion.div>
								))}
							</AnimatePresence>

							<Button
								variant="outline"
								size="icon"
								className="absolute -left-2 md:-left-4 lg:left-0 top-1/2 -translate-y-1/2 bg-background/90 hover:bg-background shadow-xl rounded-full z-20 w-12 h-12 border-2"
								onClick={handlePrev}
								aria-label="Previous review"
							>
								<ChevronLeft className="h-6 w-6" />
							</Button>
							<Button
								variant="outline"
								size="icon"
								className="absolute -right-2 md:-right-4 lg:right-0 top-1/2 -translate-y-1/2 bg-background/90 hover:bg-background shadow-xl rounded-full z-20 w-12 h-12 border-2"
								onClick={handleNext}
								aria-label="Next review"
							>
								<ChevronRight className="h-6 w-6" />
							</Button>
						</div>

						<div className="md:hidden relative w-full px-2 min-h-90 flex items-center justify-center">
							<div className="flex items-center justify-center w-full gap-1">
								<Button
									variant="ghost"
									size="icon"
									className="mr-0.5 h-12 w-12 shrink-0 rounded-full active:scale-95 transition-transform"
									onClick={handlePrev}
									aria-label="Previous review"
								>
									<ChevronLeft className="h-6 w-6" />
								</Button>

								<div className="flex-1 max-w-85 relative h-85">
									<AnimatePresence mode="wait" custom={direction}>
										<motion.div
											key={currentIndex}
											custom={direction}
											initial={{ opacity: 0 }}
											animate={{ opacity: 1 }}
											exit={{ opacity: 0 }}
											transition={{ duration: 0.8, ease: "easeInOut" }}
											className="w-full h-full absolute inset-0"
										>
											<ReviewCard review={mobileReview} />
										</motion.div>
									</AnimatePresence>
								</div>

								<Button
									variant="ghost"
									size="icon"
									className="ml-0.5 h-12 w-12 shrink-0 rounded-full active:scale-95 transition-transform"
									onClick={handleNext}
									aria-label="Next review"
								>
									<ChevronRight className="h-6 w-6" />
								</Button>
							</div>

							<div className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex gap-1.5">
								{reviews.map((_, idx) => (
									<div
										// biome-ignore lint/suspicious/noArrayIndexKey: Indicators
										key={idx}
										className={cn(
											"w-2 h-2 rounded-full transition-all duration-300",
											idx === currentIndex ? "bg-primary w-4" : "bg-muted",
										)}
									/>
								))}
							</div>
						</div>
					</>
				) : (
					<div className="text-center text-muted-foreground py-10">
						No reviews available at the moment.
					</div>
				)}
			</motion.div>

			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
				transition={{ duration: 0.5, delay: 0.4 }}
				className="pt-8 text-center relative z-20"
			>
				<Button
					size="lg"
					className="rounded-full px-8 font-bold shadow-lg transition-transform hover:scale-105 gap-2"
					asChild
				>
					<Link href={gmapsUrl} target="_blank" rel="noopener noreferrer">
						{SECTION_CONTENT.ctaText} <ArrowRight className="w-4 h-4" />
					</Link>
				</Button>
			</motion.div>
		</section>
	);
}

// biome-ignore lint/suspicious/noExplicitAny: Dynamic data from API
function ReviewCard({ review }: { review: any }) {
	return (
		<Card className="bg-card border-border hover:bg-accent transition-colors duration-300 h-full shadow-sm hover:shadow-md">
			<CardContent className="p-6 flex flex-col gap-6 h-full justify-between">
				<div className="flex gap-1">
					{[...Array(Math.round(review.rating))].map((_, starIndex) => (
						<Star
							// biome-ignore lint/suspicious/noArrayIndexKey: Stars are uniform
							key={starIndex}
							className="w-5 h-5 fill-yellow-400 text-yellow-400"
						/>
					))}
				</div>
				<p className="text-lg md:text-xl font-medium leading-relaxed text-muted-foreground line-clamp-4">
					&ldquo;{review.snippet}&rdquo;
				</p>
				<div className="mt-auto flex items-center gap-3">
					<Avatar className="h-10 w-10 border border-input">
						{review.user.thumbnail ? (
							<AvatarImage src={review.user.thumbnail} alt={review.user.name} />
						) : null}
						<AvatarFallback className="bg-primary text-primary-foreground font-bold">
							{review.user.name.charAt(0)}
						</AvatarFallback>
					</Avatar>
					<div>
						<p className="font-bold text-card-foreground line-clamp-1">
							{review.user.name}
						</p>
						<p className="text-xs text-blue-400 flex items-center gap-1">
							<CheckIcon /> {SECTION_CONTENT.verifiedText}
						</p>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}

function CheckIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="12"
			height="12"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="3"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<polyline points="20 6 9 17 4 12" />
		</svg>
	);
}
